import { Component, Input } from '@angular/core';
import { DynamicBannerConfiguration } from '../../../models/base-properties/dynamic-banner-config.interface';
import { DynamicBannerContent } from '../../../models/base-properties/dynamic-banner-content.interface';
import { DynamicBannerStyleProperties } from '../../../models/base-properties/dynamic-banner-style.interface';
import { CSSProperties } from '../../../models/styles.interfaces';
import { BaseProperties } from '../../../models/ui.interfaces';
import { BannerHeader, BannerRow } from './models/banner-alert.interface';

/**
 * This component is responsible to display alert banner
 * (image with heading, description and more info)
 */
@Component({
  selector: 'idp-banner-alert',
  templateUrl: './banner-alert.component.html',
  styleUrls: ['./banner-alert.component.scss']
})
export class BannerAlertComponent
  implements
    BaseProperties<
      DynamicBannerConfiguration,
      DynamicBannerContent,
      DynamicBannerStyleProperties
    >
{
  /** This props holds all content regarding alert image banner */
  @Input() content = {} as DynamicBannerContent;
  /**
   * This is the primary configuration for alert image banner.
   * You can pass several configuration like device configuration and template configuration
   */
  @Input() config = {} as DynamicBannerConfiguration;
  /** This holds style properties. You can set apply styling according to the use case */
  @Input() styles = {} as DynamicBannerStyleProperties;

  constructor() {
    /** Set default properties for config, content and styles */
    this.initializeContent();
    this.initializeConfig();
    this.initializeStyles();
  }

  /** Initialize content regarding this component */
  private initializeContent() {
    this.content = {
      imageContent: {
        desktop: { src: '', alt: '' },
        mobile: { src: '', alt: '' },
        tablet: { src: '', alt: '' }
      },
      bannerIndex: '',
      description: '',
      headerText: '',
      badgeText: '',
      mobilePlacementID: '',
      moreText: '',
      level: ''
    };
  }

  /** Initialize config regarding this component */
  private initializeConfig() {
    this.config = {
      deviceConfig: {
        desktopEnabled: true,
        mobileEnabled: true,
        responsive: true
      },
      templateType: 'alertbanner'
    };
  }

  /** Initialize style regarding this component */
  private initializeStyles() {
    this.styles = {
      banner: { backgroundColor: '', borderColor: '' },
      heading: { color: '', position: 'left' },
      description: {
        color: '',
        fontSize: '',
        position: 'left'
      },
      badge: {
        position: 'left'
      },
      textPosition: {
        position: 'left'
      }
    };
  }

  /** Prepare dynamic class for which the banner can be shift to left or right based on the position */
  get bannerRowClass(): BannerRow {
    let flexRow = false;
    let bannerBorder = false;
    if (
      this.styles.textPosition &&
      this.styles.textPosition.position === 'left'
    ) {
      flexRow = true;
    }
    bannerBorder = this.getBannerBorder(this.styles.banner);
    return { flexRowReverse: flexRow, hasBorder: bannerBorder };
  }

  getBannerBorder(banner: CSSProperties | undefined): boolean {
    return banner && banner.borderColor ? true : false;
  }

  /** Check desktop image setting is enabled of not */
  get desktopBannerEnabled(): boolean {
    let desktopImageEnabled = false;
    if (
      this.config.deviceConfig.desktopEnabled &&
      this.content?.imageContent?.desktop?.src
    ) {
      desktopImageEnabled = true;
    }
    return desktopImageEnabled;
  }

  /**  check mobile image setting is enabled of not */
  get mobileBannerEnabled(): boolean {
    return this.config.deviceConfig.mobileEnabled &&
      this.content?.imageContent?.mobile?.src
      ? true
      : false;
  }

  /**  prepare header styling according to the inputs */
  get headerStyles(): CSSProperties {
    return {
      color: this.styles?.heading?.color || '',
      position: this.styles?.heading?.position || 'left'
    };
  }

  /**  prepare header content for full image banner */
  get headerContent(): BannerHeader | undefined {
    return {
      text: this.content.headerText || '',
      level: this.content.level || ''
    };
  }
}
